import { getDevIdentityEmail } from '@/lib/devIdentity';
import { OpenSubmissionTabs } from './OpenSubmissionTabs';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAccount, useIsAuthenticated, useMsal } from '@azure/msal-react';
import { isAuthEnabled } from '@/env';
import { useUserPermissions } from '@/hooks/useUserPermissions';
import { useSetting } from '@/hooks/useSetting';
import { currentNavPath } from '@/lib/navCurrentPath';
import { Wordmark } from './Wordmark';
import { startSignOut } from '@/lib/auth/signOut';
import { BellIcon } from './notifications/BellIcon';
import { SessionTimeoutWarning } from './SessionTimeoutWarning';
import { AssistantPanel } from './assistant/AssistantPanel';
import { DevUserSwitcher } from './DevUserSwitcher';
import { v2 } from '@/api/v2Client';

/**
 * Phase 5.7 Wave A — restored legacy V1 navy header.
 * - Hamburger menu (Workspace section only; Admin section deferred to Wave D)
 * - AutoeCTD wordmark with light-blue "e"
 * - Notification bell (visual placeholder — Wave B/C wires the backend)
 * - Profile-initials avatar with name/email/Settings/Sign Out dropdown
 */

const OVERLAY_STYLE = {
  position: 'fixed' as const,
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 199,
};

const DROPDOWN_BASE = {
  position: 'absolute' as const,
  top: '100%',
  marginTop: 8,
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 10,
  boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
  zIndex: 200,
  overflow: 'hidden',
};

function MenuButton({
  icon,
  label,
  onClick,
  disabled,
  destructive,
}: {
  icon: ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  destructive?: boolean;
}): ReactElement {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        width: '100%',
        padding: '10px 20px',
        border: 'none',
        background: 'none',
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontSize: 14,
        color: destructive ? 'var(--red)' : 'var(--text)',
        opacity: disabled ? 0.5 : 1,
        textAlign: 'left',
      }}
      onMouseEnter={(e) => {
        if (!disabled) e.currentTarget.style.background = 'var(--surface2)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'none';
      }}
    >
      <span style={{ fontSize: 16, width: 20, textAlign: 'center' }} aria-hidden="true">
        {icon}
      </span>
      {label}
    </button>
  );
}

function SectionLabel({ text }: { text: string }): ReactElement {
  return (
    <div
      style={{
        fontSize: 10,
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        color: '#9CA3AF',
        padding: '6px 14px 2px',
        pointerEvents: 'none',
        userSelect: 'none',
        fontWeight: 600,
      }}
    >
      {text}
    </div>
  );
}

// ----- T-16 "Navigation drawer itself" -------------------------------------
// The drawer is a <nav> landmark of router links (T16-D3), not role="menu":
// it is navigation, so it gets real hrefs, Tab order and aria-current rather
// than menu semantics it never implemented. Same look and order as before;
// the only visual addition is the current-page highlight (T16-D2).

/** Drawer item look. Every item reserves a 3px left accent so the current
 *  item's bar does not shift its text (17px + 3px = the old 20px inset). */
function navItemStyle(active: boolean): CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    width: '100%',
    boxSizing: 'border-box',
    padding: '4px 20px 4px 17px',
    border: 'none',
    borderLeft: active ? '3px solid var(--accent, #2563eb)' : '3px solid transparent',
    background: active ? 'rgba(37, 99, 235, 0.10)' : 'none',
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: active ? 600 : 400,
    color: 'var(--text)',
    textAlign: 'left',
    textDecoration: 'none',
  };
}

function hoverHandlers(active: boolean): {
  onMouseEnter: (e: { currentTarget: HTMLElement }) => void;
  onMouseLeave: (e: { currentTarget: HTMLElement }) => void;
} {
  return {
    onMouseEnter: (e) => {
      if (!active) e.currentTarget.style.background = 'var(--surface2)';
    },
    onMouseLeave: (e) => {
      e.currentTarget.style.background = active ? 'rgba(37, 99, 235, 0.10)' : 'none';
    },
  };
}

function NavIcon({ icon }: { icon: ReactNode }): ReactElement {
  const paths: Record<string, string> = {
    '🏠': 'M3 10 12 3l9 7M5 9v12h5v-7h4v7h5V9',
    '📅': 'M4 5h16v16H4ZM8 3v4m8-4v4M4 10h16M8 14h2m4 0h2M8 17h2',
    '＋': 'M12 5v14M5 12h14',
    '⬇': 'M12 3v12m-5-5 5 5 5-5M4 17v4h16v-4',
    '✨': 'm12 3 2.5 6.5L21 12l-6.5 2.5L12 21l-2.5-6.5L3 12l6.5-2.5Z',
    '🔔': 'M5 17h14l-2-4V9a5 5 0 0 0-10 0v4Zm5 3h4',
    '👥': 'M16 21v-3a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v3M14 4a4 4 0 0 1 0 8m4 3a4 4 0 0 1 3 4v2M13 7a4 4 0 1 1-8 0 4 4 0 0 1 8 0',
    '🔑': 'M14 4a5 5 0 1 1-3 9l-7 7H2v-3l7-7a5 5 0 0 1 5-6Zm2 3h.01',
    '📜': 'M6 3h12v18H6ZM9 7h6M9 11h6M9 15h4',
    '🩺': 'M3 12h4l3-8 4 16 3-8h4',
    '🏢': 'M5 21V3h14v18M3 21h18M9 7h1m4 0h1M9 11h1m4 0h1M10 21v-5h4v5',
    '📝': 'M14 3H5v18h14v-9M9 15l1-4 9-9 3 3-9 9Z',
    '🔬': 'M9 3h6v4l5 12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2L9 7ZM8 14h8',
    '✓': 'm4 12 5 5L20 6',
    '📁': 'M3 7V4h6l3 3h9v14H3Z',
  };
  const path = typeof icon === 'string' ? paths[icon] : undefined;
  return <span style={{ width: 20, flexShrink: 0, display: 'inline-flex', justifyContent: 'center' }} aria-hidden="true">
    {path ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d={path} /></svg> : icon}
  </span>;
}

interface DrawerLink {
  readonly label: string;
  readonly icon: string;
  readonly to: string;
  readonly badge?: ReactNode;
}

const FADE_BASE: CSSProperties = {
  position: 'absolute',
  left: 0,
  right: 0,
  height: 28,
  pointerEvents: 'none',
  zIndex: 1,
};

export function AppShell(): ReactElement {
  const [submissionTabHeader, setSubmissionTabHeader] = useState<HTMLDivElement | null>(null);
  const isAuthed = useIsAuthenticated();
  const { accounts } = useMsal();
  const account = useAccount(accounts[0]);
  const navigate = useNavigate();
  const perms = useUserPermissions();
  const workspaceScope = isAuthEnabled() ? `${account?.tenantId ?? ''}:${account?.homeAccountId ?? 'signed-out'}` : `dev:${getDevIdentityEmail() ?? 'default'}`;
  // phase-role-access-audit item F2 note — this checks Users:READ (which
  // Phase 5.8 granted to every system role), not Users:manage; the name is
  // historical. Whether the Users/Roles items should require more is a
  // product decision escalated to Sharon — not changed here.
  const canManageUsers = perms.hasPermission('Users', 'read');
  const canReadAudit = perms.hasPermission('AuditLog', 'read');
  // phase-role-access-audit-and-full-role-coverage (F3) — each admin item is
  // gated on the SAME permission its page checks (one evaluator, two
  // consumers), so the menu never offers a screen the page will refuse.
  // Before this, five items rendered ungated under a stale "ComingSoon
  // placeholders" comment — e.g. the auditor role saw "Validation Rules"
  // but the page PermissionDenied it (auditor has no ValidationRules:read).
  const canReadCompanyProfiles = perms.hasPermission('CompanyProfiles', 'read');
  // T-07 (D-27) — Form Templates opens for FormTemplates.read (templates) OR
  // AdminForms.read (government forms, submission requirements), as the page does.
  const canReadFormTemplates =
    perms.hasPermission('FormTemplates', 'read') || perms.hasPermission('AdminForms', 'read');
  const showAdminSection = canManageUsers || canReadAudit || canReadFormTemplates;
  const canReadPrompts = perms.hasPermission('Prompts', 'read');
  const canReadValidationRules = perms.hasPermission('ValidationRules', 'read');
  const canReadSpecFiles = perms.hasPermission('SpecFiles', 'read');

  // phase-a5 — tenant off-switch. Only an explicit org `false` hides the
  // assistant surfaces (default/loading keeps them visible; the server is
  // the enforcement point either way).
  const assistantSetting = useSetting<boolean>('assistantEnabled');
  const assistantEnabled = assistantSetting.value !== false;


  // phase-fix-local-dev-menu-parity — navigation and identity are separate
  // concerns, so they get separate flags.
  //
  // With auth disabled (local dev) the backend's RBAC middleware passes every
  // permission check, so the nav must be reachable to match — suppressing the
  // hamburger left local dev with no menu at all while the backend would have
  // allowed every page.
  const showNav = isAuthEnabled() ? isAuthed : true;
  // The avatar/profile menu genuinely needs a real account (initials,
  // sign-out), so it stays suppressed in dev; the header renders a
  // "Dev mode" label in its place.
  const showUserChrome = isAuthEnabled() ? isAuthed : false;

  const [desktopNav, setDesktopNav] = useState(() =>
    typeof window.matchMedia === 'function' && window.matchMedia('(min-width: 768px)').matches,
  );
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const sidebarVisible = showNav && desktopNav;
  const [menuOpen, setMenuOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  // phase-a2-assistant-panel — the AI Assistant slide-over. Route context:
  // any /submissions/:id/... path scopes the panel to that submission
  // (covers both the :id and :submissionId param spellings by matching the
  // pathname directly).
  const [assistantOpen, setAssistantOpen] = useState(() => {
    try {
      return localStorage.getItem('autoectd.assistant-open.v1') === 'true';
    } catch { return false; }
  });
  useEffect(() => {
    try {
      localStorage.setItem('autoectd.assistant-open.v1', String(assistantOpen));
    } catch { /* A blocked/full store must not prevent using the assistant. */ }
  }, [assistantOpen]);
  const [assistantPreferredWidth, setAssistantPreferredWidth] = useState(() => {
    try {
      const saved = Number(localStorage.getItem('autoectd.assistant-width.v1'));
      if (Number.isFinite(saved) && saved >= 320 && saved <= 640) return saved;
    } catch { /* Storage may be unavailable; resizing still works in memory. */ }
    return 420;
  });
  useEffect(() => {
    try {
      localStorage.setItem('autoectd.assistant-width.v1', String(assistantPreferredWidth));
    } catch { /* A blocked/full store must not prevent using the assistant. */ }
  }, [assistantPreferredWidth]);
  const [assistantViewportWidth, setAssistantViewportWidth] = useState(() => document.documentElement.clientWidth);
  useEffect(() => {
    const update = (): void => setAssistantViewportWidth(document.documentElement.clientWidth);
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);
  const assistantMaxWidth = Math.max(320, Math.min(640, assistantViewportWidth - (sidebarVisible ? (sidebarCollapsed ? 72 : 246) : 0) - 640));
  const assistantWidth = Math.min(assistantPreferredWidth, assistantMaxWidth);
  const [assistantDockable, setAssistantDockable] = useState(() =>
    typeof window.matchMedia === 'function' && window.matchMedia('(min-width: 1440px)').matches);
  useEffect(() => {
    if (!window.matchMedia) return;
    const query = window.matchMedia('(min-width: 1440px)');
    const update = (): void => setAssistantDockable(query.matches);
    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);
  // feat/header-settings-button — hover / keyboard-focus state for the inline
  // styled header Settings button (no stylesheet change), and its active state.
  const [settingsHover, setSettingsHover] = useState(false);
  const [settingsFocus, setSettingsFocus] = useState(false);
  const location = useLocation();
  // feat/header-settings-button — the header Settings button highlights while
  // the Settings page (or anything under it) is the current route.
  const settingsActive = location.pathname.startsWith('/admin/settings');
  const assistantSubmissionId =
    /^\/submissions\/([^/]+)/.exec(location.pathname)?.[1] === 'new'
      ? null
      : (/^\/submissions\/([^/]+)/.exec(location.pathname)?.[1] ?? null);
  const [assistantSubmissionLabel, setAssistantSubmissionLabel] = useState<string | null>(null);
  // phase-fix-ui-screen-review-batch (Fix 1) — Submission Details and the
  // Compiler are split-pane workspaces; they get the full viewport width.
  // (Dashboard and admin pages keep the centred reading column.)
  const isWideRoute =
    /^\/submissions\/[^/]+$/.test(location.pathname) ||
    location.pathname.includes('/compiler') ||
    // phase-spec-enforcement-wiring Item 6 (Sharon 2026-08-03) — the spec
    // catalogue now carries provenance columns + three buttons per row and
    // needs the same full-width layout Submission Details uses.
    location.pathname === '/admin/spec-files';
  useEffect(() => {
    let cancelled = false;
    if (!assistantSubmissionId) {
      setAssistantSubmissionLabel(null);
      return;
    }
    v2.submissions
      .get(assistantSubmissionId)
      .then((sub) => {
        if (!cancelled) {
          setAssistantSubmissionLabel(
            `${sub.DrugName ?? 'Submission'} · Seq ${sub.SequenceNumber ?? '?'}`,
          );
        }
      })
      .catch(() => {
        if (!cancelled) setAssistantSubmissionLabel(null);
      });
    return () => {
      cancelled = true;
    };
  }, [assistantSubmissionId]);

  const displayName = account?.name ?? account?.username ?? 'User';
  const email = account?.username ?? '';
  const initials =
    (account?.name ?? '')
      .split(' ')
      .map((w) => w[0] ?? '')
      .join('')
      .toUpperCase()
      .slice(0, 2) || '?';

  const handleSignOut = (): void => {
    setProfileOpen(false);
    // THE shared sign-out (src/lib/auth/signOut.ts). Same call, same
    // argument, same behaviour — it just is not a second copy any more, now
    // that /join needs the same thing with a different return path.
    startSignOut();
  };

  // ----- T-16 navigation drawer -----
  const hamburgerRef = useRef<HTMLButtonElement | null>(null);
  const navRef = useRef<HTMLElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const autoFocusedRef = useRef<HTMLElement | null>(null);
  const [fade, setFade] = useState<{ top: boolean; bottom: boolean }>({ top: false, bottom: false });

  // The drawer's route items, in display order, with the SAME permission
  // rules as before (T-16 changes no gate and no order).
  const workspaceLinks: DrawerLink[] = [
    { label: 'Dashboard', icon: '🏠', to: '/home' },
    { label: 'New Submission', icon: '＋', to: '/submissions/new' },
    // Phase 5.25 — top-level Import workflow.
    { label: 'Import', icon: '⬇', to: '/import' },
  ];
  // Admin section — rendered when the user has at least one admin permission
  // (Users:read or AuditLog:read; every system role currently holds
  // Users:read — escalated to Sharon as finding F1). EVERY item is gated on
  // the same permission its page checks.
  const adminLinks: DrawerLink[] = [
    ...(canManageUsers ? [{ label: 'Users', icon: '👥', to: '/admin/users' }] : []),
    ...(canManageUsers ? [{ label: 'Roles', icon: '🔑', to: '/admin/roles' }] : []),
    ...(canReadAudit ? [{ label: 'Audit Log', icon: '📜', to: '/admin/audit-log' }] : []),
    // phase-a5 — assistant usage + cost audit view; same gate as Audit Log.
    ...(canReadAudit
      ? [{ label: 'Assistant Activity', icon: '✨', to: '/admin/assistant-activity' }]
      : []),
    // Phase 5.19.3 / T-10 — System Health observability page. Gated on
    // `canReadAudit` (AuditLog:read) since it's the same kind of read-only
    // observability surface as Audit Log; auditors / on-call need it without
    // user-management rights. The backend endpoint (/api/v1/health/detailed)
    // enforces the SAME AuditLog.read gate (T-10 / D-31) — it is no longer
    // auth-only — so this is one gate with two consumers (menu + page + API),
    // not a UX-only hint.
    ...(canReadAudit ? [{ label: 'System Health', icon: '🩺', to: '/admin/system-health' }] : []),
    ...(canReadCompanyProfiles
      ? [{ label: 'Company Profile', icon: '🏢', to: '/admin/company-profile' }]
      : []),
    ...(canReadFormTemplates
      ? [{ label: 'Form Templates', icon: '📝', to: '/admin/form-templates' }]
      : []),
    ...(canReadPrompts ? [{ label: 'Prompt Library', icon: '🔬', to: '/admin/prompt-library' }] : []),
    ...(canReadValidationRules
      ? [{ label: 'Validation Rules', icon: '✓', to: '/admin/validation-rules' }]
      : []),
    ...(canReadSpecFiles
      ? [{ label: 'Specification Files', icon: '📁', to: '/admin/spec-files' }]
      : []),
  ];
  const drawerPaths = [...workspaceLinks, ...(showAdminSection ? adminLinks : [])].map(
    (l) => l.to,
  );
  const currentPath = currentNavPath(location.pathname, drawerPaths);
  const drawerKey = drawerPaths.join('|');

  // T07-D17 — the drawer's open state as of the LATEST open/close, updated
  // synchronously. The auto-focus effect below can be left pending by a commit
  // that changed the item list (the Admin section arriving after /users/me).
  // If the drawer was closed before React flushed it, that stale effect still
  // saw menuOpen === true, read the hamburger focus that closeMenu had just
  // restored as "untouched", and moved focus into the drawer being removed —
  // so focus ended on <body>. Every open/close goes through setMenu.
  const menuOpenRef = useRef(false);
  const setMenu = useCallback((open: boolean): void => {
    menuOpenRef.current = open;
    setMenuOpen(open);
  }, []);

  /** T16-D3 — every close returns focus to the hamburger unless the caller
   *  hands focus elsewhere (AI Assistant → its panel input; the profile menu). */
  const closeMenu = useCallback(
    (restoreFocus: boolean): void => {
      setMenu(false);
      if (restoreFocus) hamburgerRef.current?.focus();
    },
    [setMenu],
  );

  useEffect(() => {
    if (!window.matchMedia) return;
    const query = window.matchMedia('(min-width: 768px)');
    const update = (): void => {
      setDesktopNav(query.matches);
      // A responsive close must invalidate pending drawer focus effects too.
      // Only restore focus when an open drawer is actually being removed.
      closeMenu(menuOpenRef.current);
    };
    update();
    query.addEventListener('change', update);
    window.addEventListener('resize', update);
    return () => {
      query.removeEventListener('change', update);
      window.removeEventListener('resize', update);
    };
  }, [closeMenu]);

  const updateFades = useCallback((): void => {
    const el = scrollRef.current;
    if (!el) return;
    const top = el.scrollTop > 1;
    const bottom = el.scrollTop + el.clientHeight < el.scrollHeight - 1;
    setFade((prev) => (prev.top === top && prev.bottom === bottom ? prev : { top, bottom }));
  }, []);

  // On open (and when the item list settles, e.g. the Admin section arriving
  // after /users/me): move focus to the current item, else the first, and
  // scroll it into view. Re-focus only while focus is still where we put it,
  // so a user who has already moved on is never yanked back.
  useEffect(() => {
    // menuOpenRef: a pending effect from before a close must not act (T07-D17).
    if (!menuOpen || !menuOpenRef.current) {
      autoFocusedRef.current = null;
      return;
    }
    const nav = navRef.current;
    if (!nav) return;
    const active = document.activeElement;
    const untouched =
      autoFocusedRef.current === null ||
      active === autoFocusedRef.current ||
      active === document.body ||
      active === hamburgerRef.current;
    if (untouched) {
      const target =
        nav.querySelector<HTMLElement>('[aria-current="page"]') ??
        nav.querySelector<HTMLElement>('a[href], button:not([disabled])');
      if (target) {
        target.focus();
        autoFocusedRef.current = target;
        if (typeof target.scrollIntoView === 'function') target.scrollIntoView({ block: 'nearest' });
      }
    }
    updateFades();
  }, [menuOpen, drawerKey, currentPath, updateFades]);

  // While open: Escape closes from anywhere; the fade cue tracks resizes.
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeMenu(true);
      }
    };
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', updateFades);
    return () => {
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', updateFades);
    };
  }, [menuOpen, closeMenu, updateFades]);

  /** T16-D3 — Tab / Shift+Tab stay inside the open drawer. */
  const onNavKeyDown = (e: ReactKeyboardEvent<HTMLElement>): void => {
    if (e.key !== 'Tab') return;
    const items = Array.from(
      navRef.current?.querySelectorAll<HTMLElement>('a[href], button:not([disabled])') ?? [],
    );
    if (items.length === 0) return;
    const first = items[0]!;
    const last = items[items.length - 1]!;
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const openMenu = (): void => {
    setProfileOpen(false);
    if (menuOpen) closeMenu(true);
    else setMenu(true);
  };
  const openProfile = (): void => {
    closeMenu(false);
    setProfileOpen((v) => !v);
  };

  const renderLink = (item: DrawerLink): ReactElement => {
    const active = item.to === currentPath;
    return (
      <Link
        key={item.to}
        to={item.to}
        aria-label={item.label}
        title={item.label}
        aria-current={active ? 'page' : undefined}
        onClick={() => closeMenu(true)}
        style={navItemStyle(active)}
        {...hoverHandlers(active)}
      >
        <NavIcon icon={item.icon} />
        <span className="workspace-nav-label">{item.label}</span>
        {item.badge}
      </Link>
    );
  };

  const goto = (path: string): void => {
    setMenu(false);
    setProfileOpen(false);
    navigate(path);
  };

  return (
    <div style={{ '--assistant-width': `${assistantWidth}px` } as CSSProperties} className={`app-shell workspace-shell${showNav && assistantEnabled && assistantOpen && assistantDockable ? ' workspace-shell--assistant-docked' : ''}${sidebarVisible ? ' workspace-shell--sidebar' : ''}${sidebarVisible && sidebarCollapsed ? ' workspace-shell--rail' : ''}`}>
      <a className="workspace-skip" href="#workspace-main">Skip to content</a>
      {sidebarVisible && (
        <aside className="workspace-sidebar">
          <Link to="/home" className="workspace-brand" aria-label="AutoeCTD" title="AutoeCTD">{sidebarCollapsed ? <span className="workspace-brand-icon" aria-hidden="true">A</span> : <strong><Wordmark /></strong>}</Link>
          <nav id="workspace-navigation" aria-label="Main navigation" className="workspace-navigation">
            <SectionLabel text="Workspace" />
            {workspaceLinks.map(renderLink)}
            {assistantEnabled && <button type="button" aria-label="AI Assistant" title="AI Assistant" style={navItemStyle(false)} onClick={() => setAssistantOpen(true)}><NavIcon icon="✨" /><span className="workspace-nav-label">AI Assistant</span></button>}
            {showAdminSection && <><SectionLabel text="Admin" />{adminLinks.map(renderLink)}</>}
          </nav>
          <div className="workspace-sidebar-footer">
            <Link to="/admin/settings" aria-label="Settings" title="Settings"><span aria-hidden="true">⚙</span><span className="workspace-nav-label">Settings</span></Link>

          </div>
        </aside>
      )}
      <header className="header">
        {/* Left — hamburger + logo */}
        <div className="workspace-header-navigation" style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 }}>
          {showNav && (
            <div style={{ position: 'relative' }}>
              <button
                ref={hamburgerRef}
                type="button"
                title={desktopNav ? (sidebarCollapsed ? 'Open sidebar' : 'Close sidebar') : 'Open navigation'}
                aria-label={desktopNav ? (sidebarCollapsed ? 'Expand navigation' : 'Collapse navigation') : 'Open navigation'}
                aria-expanded={desktopNav ? !sidebarCollapsed : menuOpen}
                aria-controls={sidebarVisible ? 'workspace-navigation' : menuOpen ? 'main-navigation' : undefined}
                onClick={desktopNav ? () => setSidebarCollapsed((value) => !value) : openMenu}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--primary)',
                  borderRadius: 8,
                  padding: 8,
                  width: 40,
                  height: 40,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  fontSize: 18,
                  lineHeight: 1,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(27,58,107,0.08)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
                  <rect x="3" y="4" width="18" height="16" rx="3" />
                  <path d="M9 4v16" />
                  <path d="M5.5 8h1M5.5 11h1" />
                </svg>
              </button>
              {menuOpen && (
                <>
                  <div
                    data-testid="nav-overlay"
                    style={OVERLAY_STYLE}
                    onClick={() => closeMenu(true)}
                  />
                  <nav
                    ref={navRef}
                    id="main-navigation"
                    aria-label="Main navigation"
                    onKeyDown={onNavKeyDown}
                    style={{
                      ...DROPDOWN_BASE,
                      left: 0,
                      width: 240,
                      paddingBottom: 8,
                      // The drawer's content (15+ items) is taller than a short
                      // viewport can show. Bound it by the space actually left
                      // below the header — NOT a fixed cap — and let the item
                      // list scroll inside it. 84px is the header chrome above
                      // the panel (header + marginTop + a bottom gap), so the
                      // panel grows and shrinks with the viewport.
                      display: 'flex',
                      flexDirection: 'column',
                      maxHeight: 'calc(100vh - 84px)',
                    }}
                  >
                    <div className="workspace-brand"><strong><Wordmark /></strong></div>
                    {/* T16-D1 — scroll cue. A fade at the bottom while more
                        items sit below the fold, at the top once scrolled.
                        Decorative only (aria-hidden, no pointer events). */}
                    {fade.top && (
                      <div
                        data-testid="nav-fade-top"
                        aria-hidden="true"
                        style={{
                          ...FADE_BASE,
                          top: 0,
                          background: 'linear-gradient(to bottom, var(--surface), transparent)',
                        }}
                      />
                    )}
                    <div
                      ref={scrollRef}
                      data-testid="nav-scroll"
                      onScroll={updateFades}
                      style={{
                        // min-height:0 is what lets a flex child actually
                        // shrink below its content height so overflow-y can
                        // take effect; without it the list refuses to scroll.
                        flex: '1 1 auto',
                        minHeight: 0,
                        overflowY: 'auto',
                      }}
                    >
                      <SectionLabel text="Workspace" />
                      {workspaceLinks.map(renderLink)}
                      {/* AI Assistant opens a panel — it is an action, not a
                          page, so it stays a button and is never "current".
                          Closing here does NOT restore focus to the
                          hamburger: the panel takes focus (its input). */}
                      {assistantEnabled && (
                        <button
                          type="button"
                          onClick={() => {
                            closeMenu(false);
                            setAssistantOpen(true);
                          }}
                          style={navItemStyle(false)}
                          {...hoverHandlers(false)}
                        >
                          <NavIcon icon="✨" />
                          AI Assistant
                        </button>
                      )}
                      {/* Phase forms-templates-unified: /templates nav removed.
                          The page remains accessible at /templates; it's now
                          reached via the Admin → Form Templates admin panel. */}
                      {/* Phase phase-ui-remove-compiler-menu-entry (Sharon,
                          2026-07-16): the Compiler nav entry is removed. The
                          Compiler is submission-scoped — opening it from the menu
                          hit a "no submission selected" dead end. It is reached
                          through a submission instead (Submission Details →
                          Compile Submission / View Submission). Both routes stay
                          registered and working (/compiler and
                          /submissions/:submissionId/compiler) — the entry is
                          unlisted, not deleted. Same principle as the Import
                          button removal (943e806): no menu doors to dead ends. */}
                      {showAdminSection && (
                        <>
                          <SectionLabel text="Admin" />
                          {adminLinks.map(renderLink)}
                        </>
                      )}
                    </div>
                    {fade.bottom && (
                      <div
                        data-testid="nav-fade-bottom"
                        aria-hidden="true"
                        style={{
                          ...FADE_BASE,
                          bottom: 8,
                          background: 'linear-gradient(to top, var(--surface), transparent)',
                        }}
                      />
                    )}
                  </nav>
                </>
              )}
            </div>
          )}
          {showNav && <BellIcon onOpen={() => { closeMenu(false); setProfileOpen(false); }} />}
          {!showNav && <Link to="/home" className="logo"><Wordmark /></Link>}
          <div ref={setSubmissionTabHeader} className="workspace-header-tabs" />
        </div>

        {/* Profile */}
        <div className={`header-user${sidebarVisible ? ' workspace-user-footer' : ''}`}>
          {showUserChrome && (
            <div style={{ position: 'relative' }}>
              <button
                type="button"
                aria-label="Open profile menu"
                aria-expanded={profileOpen}
                onClick={openProfile}
                title={displayName}
                className="header-avatar"
                style={{ cursor: 'pointer', border: 'none', padding: 0 }}
              >
                {initials}
                {sidebarVisible && !sidebarCollapsed && <span className="workspace-user-name">{displayName}</span>}
              </button>
              {profileOpen && (
                <>
                  <div style={OVERLAY_STYLE} onClick={() => setProfileOpen(false)} />
                  <div
                    role="menu"
                    className="workspace-profile-menu"
                    style={{
                      ...DROPDOWN_BASE,
                      right: 0,
                      width: 220,
                    }}
                  >
                    <div
                      style={{
                        padding: '16px 20px',
                        borderBottom: '1px solid var(--border)',
                      }}
                    >
                      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>
                        {displayName}
                      </div>
                      {email && (
                        <div
                          style={{
                            fontSize: 11,
                            color: 'var(--text-muted)',
                            marginTop: 2,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {email}
                        </div>
                      )}
                    </div>
                    {/* Phase 5.20d — wired up to the /admin/settings page
                        shipped in Phase 5.20a (3-tier org/user/submission
                        settings). Ungated here to match the surrounding
                        profile-dropdown items; the page itself enforces
                        any per-scope read/write permissions. */}
                    <MenuButton
                      icon="⚙"
                      label="My Settings"
                      onClick={() => goto('/admin/settings')}
                    />
                    <MenuButton icon="→" label="Sign Out" destructive onClick={handleSignOut} />
                  </div>
                </>
              )}
            </div>
          )}
          {/* phase-dev-identity-and-user-switcher — TWO guards, one purpose
              each: the RAW import.meta.env literal is replaced by Vite at
              build time, so a production build (VITE_AUTH_ENABLED=true)
              reduces the whole clause to `false && …` and the switcher is
              COMPILED AWAY, not merely hidden (verified by building with
              auth on and grepping dist for the switcher's markers). The
              isAuthEnabled() call keeps runtime behaviour uniform where the
              env is not statically known (tests). */}
          {import.meta.env.VITE_AUTH_ENABLED !== 'true' && !isAuthEnabled() && (
            <>
              {/* feat/header-settings-button — in dev mode the switcher REPLACES
                  the profile dropdown, and "My Settings" lived only inside that
                  dropdown, so /admin/settings was reachable only by typing the
                  URL. This icon button restores a visible route to Settings in
                  dev mode. It sits inside the SAME guard as the switcher, so a
                  production build (VITE_AUTH_ENABLED=true) compiles it away and
                  keeps the profile dropdown's My Settings as the only entry. No
                  permission gate here, like every other header item — the
                  Settings page enforces its own read/write permissions. */}
              <button
                type="button"
                aria-label="Settings"
                title="Settings"
                data-testid="header-settings-button"
                aria-current={settingsActive ? 'page' : undefined}
                onClick={() => goto('/admin/settings')}
                onMouseEnter={() => setSettingsHover(true)}
                onMouseLeave={() => setSettingsHover(false)}
                onFocus={() => setSettingsFocus(true)}
                onBlur={() => setSettingsFocus(false)}
                style={{
                  // Same family as the hamburger: transparent, hairline border,
                  // white glyph, 6px radius. Active route and hover both lift
                  // the background; keyboard focus draws a visible white ring.
                  background: settingsActive
                    ? 'rgba(255,255,255,0.18)'
                    : settingsHover
                      ? 'rgba(255,255,255,0.1)'
                      : 'transparent',
                  border: '1px solid rgba(255,255,255,0.3)',
                  color: 'var(--primary)',
                  borderRadius: 6,
                  padding: '6px 10px',
                  cursor: 'pointer',
                  fontSize: 16,
                  lineHeight: 1,
                  flexShrink: 0,
                  // Keep the browser's own :focus-visible outline (never
                  // suppress it) but make it white so it is visible on the
                  // navy header; the box-shadow ring reinforces it.
                  outlineColor: '#fff',
                  outlineOffset: 2,
                  boxShadow: settingsFocus ? '0 0 0 2px rgba(255,255,255,0.9)' : 'none',
                }}
              >
                ⚙
              </button>
              <DevUserSwitcher compact={sidebarVisible} showName={sidebarVisible && !sidebarCollapsed} />
            </>
          )}
        </div>
      </header>

      <main className={location.pathname.startsWith('/admin/') ? 'admin-workspace' : undefined} id="workspace-main" tabIndex={-1} style={{ minHeight: 'calc(100vh - 60px)', background: 'var(--bg)' }}>
        <div className={isWideRoute ? 'page page--wide' : 'page'}>
          <OpenSubmissionTabs key={workspaceScope} storageScope={workspaceScope} headerContainer={submissionTabHeader} />
        </div>
      </main>

      {/* Phase security-housekeeping (TODO-089) — 21 CFR Part 11 / SOC 2
          idle timeout. Inert when auth is disabled (dev mode). */}
      <SessionTimeoutWarning />

      {/* phase-a2-assistant-panel — floating assistant access on EVERY
          authenticated screen (fixed position survives the Compiler's
          full-viewport workspace; z-index 899 sits under the panel/backdrop
          it opens and under all modals). */}
      {/* T16-D7 — not rendered while the drawer is open: it sat above the
          drawer's click-away overlay, so it opened the assistant on top of an
          open drawer instead of closing it. */}
      {showNav && assistantEnabled && !assistantOpen && !menuOpen && (
        <button
          type="button"
          aria-label="Open AI Assistant"
          data-testid="assistant-fab"
          onClick={() => setAssistantOpen(true)}
          style={{
            position: 'fixed',
            right: 20,
            bottom: 20,
            width: 48,
            height: 48,
            borderRadius: '50%',
            border: '1px solid var(--border)',
            background: 'var(--accent, #2563eb)',
            color: '#fff',
            fontSize: 20,
            cursor: 'pointer',
            boxShadow: '0 4px 14px rgba(15, 23, 42, 0.25)',
            zIndex: 899,
          }}
        >
          ✨
        </button>
      )}
      {showNav && assistantEnabled && (
        <AssistantPanel
          open={assistantOpen}
          docked={assistantDockable}
          width={assistantWidth}
          maxWidth={assistantMaxWidth}
          onWidthChange={(width) => setAssistantPreferredWidth(Math.max(320, Math.min(assistantMaxWidth, width)))}
          onClose={() => setAssistantOpen(false)}
          submissionId={assistantSubmissionId}
          submissionLabel={assistantSubmissionLabel}
          defaultScope={
            assistantSubmissionId
              ? 'submission'
              : location.pathname === '/home' || location.pathname === '/'
                ? 'global'
                : 'explanatory'
          }
        />
      )}
    </div>
  );
}
